<template>
  <div class="editable-list-ui">
    <div v-if="$slots.header" class="editable-list-ui__header">
      <slot name="header" />
    </div>
    <VSpinner v-if="isLoading && !list.length" class="centered-block" />
    <VFallbackText v-else-if="hasError" class="centered">Something went wrong</VFallbackText>
    <template v-else-if="list.length">
      <div class="editable-list-ui__list">
        <slot name="table-header">
          <VTableHeader :class="variationClassName" :titles="tableHeaders" />
        </slot>
        <VList :class="variationClassName">
          <EditableListRow v-for="item in list" :key="item.id" :class="variationClassName">
            <slot name="row" :item="item" />
            <div class="editable-list-ui__row-controls">
              <DeleteBtn
                v-if="!hideDeleteBtn"
                :class="variantClassName"
                :item-description="listItemDescription"
                :disabled="isDeleteDisabled(item)"
                @confirm="emitDelete(item.id)"
              />
              <VBtn
                v-if="isEditAvailable(item)"
                :class="variantClassName"
                outline
                :disabled="isEditDisabled(item)"
                @click="emitEdit(item)"
                >Edit</VBtn
              >
            </div>
          </EditableListRow>
        </VList>
      </div>
      <VPagination v-if="pagination" :pagination="pagination" @on-go-to-page="emitFetch" />
    </template>
    <VFallbackText v-else class="centered">No data found</VFallbackText>
  </div>
</template>

<script lang="ts" setup generic="ListItem extends { id: number }">
  // Types
  import type { PropType } from 'vue';
  import type { TPagination } from '~/misc/resource/types';

  // Components
  import EditableListRow from '~/misc/editable-list/row/EditableListRow.vue';
  import VPagination from '~/misc/v-pagination/VPagination.vue';
  import VBtn from '~/misc/v-btn';
  import DeleteBtn from '~/misc/resource/delete/btn/DeleteBtn.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';
  import VTableHeader from '~/misc/v-table-header/VTableHeader.vue';
  import VList from '~/misc/v-list';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';

  const props = defineProps({
    list: {
      type: Array as PropType<ListItem[]>,
      default: () => [],
    },
    listItemDescription: {
      type: String,
      default: '',
    },
    tableHeaders: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    pagination: {
      type: Object as PropType<TPagination | null>,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    isEditAvailable: {
      type: Function as PropType<(item: ListItem) => boolean>,
      default: () => true,
    },
    isEditDisabled: {
      type: Function as PropType<(item: ListItem) => boolean>,
      default: () => false,
    },
    isDeleteDisabled: {
      type: Function as PropType<(item: ListItem) => boolean>,
      default: () => false,
    },
    variationClassName: {
      type: String,
      default: '',
    },
    hideDeleteBtn: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits<{
    (e: 'fetch', page: number): void;
    (e: 'edit', item: ListItem): void;
    (e: 'delete', id: number): void;
  }>();

  const emitFetch = (page: number) => emit('fetch', page);

  const emitEdit = (item: ListItem) => emit('edit', item);

  const emitDelete = (id: number) => emit('delete', id);

  const variantClassName: ComputedRef<string> = computed<string>(() =>
    [
      'manager-user-resource',
      'talking-point-resource',
      'manager-patches',
      'manager-company-sources',
    ].includes(props.variationClassName)
      ? 'slim'
      : props.variationClassName ?? ''
  );
</script>
